import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  EmailLabelRule,
  GetEmailLabelRulesResponse,
  GetEmailNotificationPreferencesResponse,
  GetOnboardingStatusResponse,
  ToggleAutoDraftRepliesResponse,
  ToggleEmailLabellingResponse,
  UpdateArchivingExceptionsResponse,
  UpdateEmailNotificationPreferencesResponse,
} from '@dreamai/types';
import api from '../../api';
import EmailTagRules, { BUILT_IN_CLASSIFICATIONS } from './email/EmailTagRules';
import AlertsTab from './email/AlertsTab';
import DraftingTab from './email/DraftingTab';
import InboxesTab, { InboxAccount } from './email/InboxesTab';
import { ReactComponent as AlertTriangleIcon } from '../../icons/alert-triangle-outline.svg';
import './inbox-management-page.scss';
import './canvas/project-app-generated-page.scss';

interface InboxManagementPageProps {
  /** Hide the internal "Inbox Manager" heading (e.g. when rendered inside the app-view layout). */
  hideHeading?: boolean;
}

interface TabDefinition {
  id: string;
  label: string;
  /** Shorter label used on narrow layouts. */
  narrowLabel?: string;
}

const TABS: TabDefinition[] = [
  { id: 'tags', label: 'Tags & Archiving', narrowLabel: 'Tags' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'drafting', label: 'Drafting' },
  { id: 'inboxes', label: 'Inboxes' },
];

const InboxManagementPage: React.FC<InboxManagementPageProps> = ({
  hideHeading,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rules, setRules] = useState<EmailLabelRule[]>([]);
  const [accounts, setAccounts] = useState<InboxAccount[]>([]);
  const [notificationDescription, setNotificationDescription] = useState<
    string | null
  >(null);
  const [assistantEmail, setAssistantEmail] = useState<string | null>(null);
  const [assistantName, setAssistantName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [togglingOauthId, setTogglingOauthId] = useState<number | null>(null);
  const [togglingAutoDraftOauthId, setTogglingAutoDraftOauthId] = useState<
    number | null
  >(null);

  const activeTab = searchParams.get('tab') || TABS[0].id;

  const customRules = useMemo(
    () => rules.filter((r) => r.description !== null),
    [rules],
  );

  const tagsCount = BUILT_IN_CLASSIFICATIONS.length + customRules.length;

  const tabCounts: Record<string, number | undefined> = {
    tags: tagsCount,
    inboxes: accounts.length,
  };

  const hasDisconnectedInbox = accounts.some((a) => a.isActive === false);

  const fetchData = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const [rulesRes, prefsRes, statusRes] = await Promise.all([
        api.get<GetEmailLabelRulesResponse>('/email-label-rules', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get<GetEmailNotificationPreferencesResponse>(
          '/email-notification-preferences',
          { headers: { Authorization: `Bearer ${token}` } },
        ),
        api.get<GetOnboardingStatusResponse>('/onboarding/status', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      if (rulesRes.data.success) {
        setRules(rulesRes.data.rules);
        setAccounts(rulesRes.data.accounts);
      }

      if (prefsRes.data.success) {
        setNotificationDescription(prefsRes.data.preferences.description);
      }

      if (statusRes.data.success) {
        setAssistantEmail(statusRes.data.status.assistantEmail);
        setAssistantName(
          statusRes.data.status.assistantNickname ||
            statusRes.data.status.assistantName,
        );
      }
    } catch (err) {
      console.error('Error loading inbox management data:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setTab = useCallback(
    (tabId: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tabId);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const startGoogleOAuth = async () => {
    try {
      const token = await getAccessTokenSilently();
      const { data } = await api.get<{ url: string }>(
        '/google-link/initiate-google-oauth?scope=email&page_id=settings',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      window.location.href = data.url;
    } catch (err) {
      console.error('Error starting Google connect flow:', err);
    }
  };

  const handleToggleLabelling = async (account: InboxAccount) => {
    if (togglingOauthId !== null) return;
    setTogglingOauthId(account.oauthId);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.patch<ToggleEmailLabellingResponse>(
        '/email-label-rules/toggle-labelling',
        { oauthId: account.oauthId, enabled: !account.emailLabelling },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        setAccounts((prev) =>
          prev.map((a) =>
            a.oauthId === response.data.oauthId
              ? { ...a, emailLabelling: response.data.emailLabellingEnabled }
              : a,
          ),
        );
      }
    } catch (err) {
      console.error('Error toggling email labelling:', err);
    } finally {
      setTogglingOauthId(null);
    }
  };

  const handleToggleAutoDraft = async (account: InboxAccount) => {
    if (togglingAutoDraftOauthId !== null) return;
    setTogglingAutoDraftOauthId(account.oauthId);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.patch<ToggleAutoDraftRepliesResponse>(
        '/email-label-rules/toggle-auto-draft',
        { oauthId: account.oauthId, enabled: !account.autoDraftReplies },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        setAccounts((prev) =>
          prev.map((a) =>
            a.oauthId === response.data.oauthId
              ? {
                  ...a,
                  autoDraftReplies: response.data.autoDraftRepliesEnabled,
                }
              : a,
          ),
        );
      }
    } catch (err) {
      console.error('Error toggling auto-draft replies:', err);
    } finally {
      setTogglingAutoDraftOauthId(null);
    }
  };

  const handleSaveArchivingExceptions = async (
    account: InboxAccount,
    value: string | null,
  ) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await api.patch<UpdateArchivingExceptionsResponse>(
        '/email-label-rules/archiving-exceptions',
        { oauthId: account.oauthId, archivingExceptions: value },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        setAccounts((prev) =>
          prev.map((a) =>
            a.oauthId === response.data.oauthId
              ? { ...a, archivingExceptions: response.data.archivingExceptions }
              : a,
          ),
        );
      }
    } catch (err) {
      console.error('Error saving archiving exceptions:', err);
    }
  };

  const handleSaveNotificationDescription = async (value: string | null) => {
    try {
      const token = await getAccessTokenSilently();
      const response =
        await api.put<UpdateEmailNotificationPreferencesResponse>(
          '/email-notification-preferences',
          { description: value },
          { headers: { Authorization: `Bearer ${token}` } },
        );
      if (response.data.success) {
        setNotificationDescription(response.data.preferences.description);
      }
    } catch (err) {
      console.error('Error saving notification preferences:', err);
    }
  };

  if (loading) {
    return <div className='inbox-management-page loading'></div>;
  }

  return (
    <div className='inbox-management-page'>
      {!hideHeading && (
        <div className='inbox-management-heading'>
          <h1>Inbox Manager</h1>
        </div>
      )}

      <nav className='project-app-tabs' role='tablist'>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const count = tabCounts[tab.id];
          return (
            <button
              key={tab.id}
              type='button'
              role='tab'
              aria-selected={isActive}
              className={`project-app-tab${isActive ? ' project-app-tab--active' : ''}`}
              onClick={() => setTab(tab.id)}
              data-testid={`inbox-tab-${tab.id}`}
            >
              <span className='label-wide'>{tab.label}</span>
              <span className='label-narrow'>
                {tab.narrowLabel || tab.label}
              </span>
              {tab.id === 'inboxes' && hasDisconnectedInbox && (
                <span
                  className='project-app-tab-warning'
                  title='An inbox is disconnected'
                >
                  <AlertTriangleIcon />
                </span>
              )}
              {count !== undefined && count > 0 && (
                <span className='project-app-tab-count'>{count}</span>
              )}
            </button>
          );
        })}
      </nav>

      <div className='inbox-management-body'>
        {activeTab === 'tags' && (
          <div className='inbox-tab tags-tab'>
            <EmailTagRules
              rules={rules}
              onRulesChange={setRules}
              onGoToInboxes={() => setTab('inboxes')}
            />
          </div>
        )}

        {activeTab === 'alerts' && (
          <AlertsTab
            description={notificationDescription}
            onSave={handleSaveNotificationDescription}
          />
        )}

        {activeTab === 'drafting' && (
          <DraftingTab
            accounts={accounts}
            togglingAutoDraftOauthId={togglingAutoDraftOauthId}
            onToggleAutoDraft={handleToggleAutoDraft}
            assistantEmail={assistantEmail}
            assistantName={assistantName}
          />
        )}

        {activeTab === 'inboxes' && (
          <InboxesTab
            accounts={accounts}
            togglingOauthId={togglingOauthId}
            onToggleLabelling={handleToggleLabelling}
            onSaveArchivingExceptions={handleSaveArchivingExceptions}
            onConnectInbox={startGoogleOAuth}
            onReconnectInbox={startGoogleOAuth}
          />
        )}
      </div>
    </div>
  );
};

export default InboxManagementPage;
